import { object } from '@art-suite/art-core-ts';

import { ScanOrScanTemplatePartTypeSchema } from '../CommonScanSchemas';
import { Scope } from '../OptimizedValueExpressions';
import { getEffectiveScanValue, getValueTypeFromScanValue, SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH, ScanPart, ScanValues } from '../ScanRoot';
import { isCalculatableResult } from '../ScanTemplateRoot';

import {
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from './ScanRootOptimizedSchemas';

export const partIsApplicable = (part: ScanPartOptimized) => !part.notApplicable;

export const getScanGroupCountByType = (scan: ScanRootOptimized, groupTemplateId: string): number =>
  scan.groups?.filter(g => g.templateId === groupTemplateId).length ?? 0;

export const getScanPartIsOptimized = (scanPart: ScanPart): scanPart is ScanPartOptimized => {
  return (scanPart as any).optimizedValueExpressions !== undefined;
};

export const getOptimizedGroupsById = (groups: ScanGroupOptimized[]): Record<string, ScanGroupOptimized> => object(groups, { withKey: group => group.id });
export const getOptimizedTasksById = (tasks: ScanTaskOptimized[]): Record<string, ScanTaskOptimized> => object(tasks, { withKey: task => task.id });
export const getOptimizedOptionsById = (options: ScanOptionOptimized[]): Record<string, ScanOptionOptimized> =>
  object(options, { withKey: option => option.id });
export const getOptimizedLineItemsById = (lineItems: ScanLineItemOptimized[]): Record<string, ScanLineItemOptimized> =>
  object(lineItems, { withKey: lineItem => lineItem.id });

export const getScanPartChildren = (scanPart: ScanPartOptimized): ScanPart[] => {
  switch (scanPart.scanPartType) {
    case 'scan':
      return (scanPart as ScanRootOptimized).groups;
    case 'group':
      return (scanPart as ScanGroupOptimized).tasks;
    case 'task':
      return (scanPart as ScanTaskOptimized).options;
    case 'option':
      return (scanPart as ScanOptionOptimized).lineItems;
    case 'lineItem':
      return [];
    default:
      throw new Error(`Invalid scan part type: ${scanPart.scanPartType}`);
  }
};

/**
 * Returns the type of scan part that the photo belongs to given the chain of ids leading to it
 * @param groupId
 * @param taskId
 * @param optionId
 * @param lineItemId
 * @returns
 */
export const getScanPartType = (groupId?: string, taskId?: string, optionId?: string, lineItemId?: string): ScanOrScanTemplatePartTypeSchema =>
  !groupId
    ? 'scan'
    : !taskId
    ? 'group'
    : !optionId
    ? 'task'
    : !lineItemId
    ? 'option'
    : 'lineItem';

export const getScanPartTypeFromDepth = (depth: number): ScanOrScanTemplatePartTypeSchema => {
  switch (depth) {
    case 0:
      return 'scan';
    case 1:
      return 'group';
    case 2:
      return 'task';
    case 3:
      return 'option';
    case 4:
      return 'lineItem';
    default:
      throw new Error(`Invalid depth: ${depth}`);
  }
};

// **************************************************************
// Easy asserting-getters given you have their parents
// **************************************************************
export const getScanGroupFromScan = (scan: ScanRootOptimized, groupId: string): ScanGroupOptimized => {
  const group = scan.groupsById[groupId];
  if (!group) throw new Error(`Group with id ${groupId} not found`);
  return group;
};

export const getScanTaskFromGroup = (group: ScanGroupOptimized, taskId: string): ScanTaskOptimized => {
  const task = group.tasksById[taskId];
  if (!task) throw new Error(`Task with id ${taskId} not found`);
  return task;
};

export const getScanOptionFromTask = (task: ScanTaskOptimized, optionId: string): ScanOptionOptimized => {
  const option = task.optionsById[optionId];
  if (!option) throw new Error(`Option with id ${optionId} not found`);
  return option;
};

export const getScanLineItemFromOption = (option: ScanOptionOptimized, lineItemId: string): ScanLineItemOptimized => {
  const lineItem = option.lineItemsById[lineItemId];
  if (!lineItem) throw new Error(`Line item with id ${lineItemId} not found`);
  return lineItem;
};

// **************************************************************
// asserting-Getters pathed from the Scan and a sequence of ids
// **************************************************************
export const getScanGroup = (scan: ScanRootOptimized, groupId: string): ScanGroupOptimized => {
  const group = scan.groupsById[groupId];
  if (!group) throw new Error(`Group with id ${groupId} not found`);
  return group;
};

export const getScanGroupOrUndefined = (scan: ScanRootOptimized | null | undefined, groupId: string | undefined | null): ScanGroupOptimized | undefined => {
  if (!scan || !groupId) return undefined;
  return scan.groupsById[groupId];
};

export const getScanTask = (scan: ScanRootOptimized, groupId: string, taskId: string, group?: ScanGroupOptimized): ScanTaskOptimized => {
  if (!group) group = getScanGroup(scan, groupId);
  const task = group.tasksById[taskId];
  if (!task) throw new Error(`Task with id ${taskId} not found`);
  return task;
};

export const getScanTaskOrUndefined = (
  scan: ScanRootOptimized | null | undefined,
  groupId: string | undefined | null,
  taskId: string | undefined | null,
): ScanTaskOptimized | undefined => {
  const group = getScanGroupOrUndefined(scan, groupId);
  if (!group || !taskId) return undefined;
  return group.tasksById[taskId];
};

export const getScanOption = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  group?: ScanGroupOptimized,
  task?: ScanTaskOptimized,
): ScanOptionOptimized => {
  if (!task) task = getScanTask(scan, groupId, taskId, group);
  const option = task.optionsById[optionId];
  if (!option) throw new Error(`Option with id ${optionId} not found`);
  return option;
};

export const getScanOptionOrUndefined = (
  scan: ScanRootOptimized | null | undefined,
  groupId: string | undefined | null,
  taskId: string | undefined | null,
  optionId: string | undefined | null,
): ScanOptionOptimized | undefined => {
  const task = getScanTaskOrUndefined(scan, groupId, taskId);
  if (!task || !optionId) return undefined;
  return task.optionsById[optionId];
};

export const getScanLineItem = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  lineItemId: string,
  group?: ScanGroupOptimized,
  task?: ScanTaskOptimized,
  option?: ScanOptionOptimized,
): ScanLineItemOptimized => {
  if (!option) option = getScanOption(scan, groupId, taskId, optionId, group, task);
  const lineItem = option.lineItemsById[lineItemId];
  if (!lineItem) throw new Error(`Line item with id ${lineItemId} not found`);
  return lineItem;
};

export const getScanLineItemOrUndefined = (
  scan: ScanRootOptimized | null | undefined,
  groupId: string | undefined | null,
  taskId: string | undefined | null,
  optionId: string | undefined | null,
  lineItemId: string | undefined | null,
): ScanLineItemOptimized | undefined => {
  const option = getScanOptionOrUndefined(scan, groupId, taskId, optionId);
  if (!option || !lineItemId) return undefined;
  return option.lineItemsById[lineItemId];
};

export const getScanPart = (scan: ScanRootOptimized, groupId?: string, taskId?: string, optionId?: string, lineItemId?: string): ScanPartOptimized => {
  if (!groupId) return scan;
  const group = scan.groupsById[groupId];
  if (!taskId) return group;
  const task = group.tasksById[taskId];
  if (!optionId) return task;
  const option = task.optionsById[optionId];
  if (!lineItemId) return option;
  return option.lineItemsById[lineItemId];
};

// **************************************************************
// asserting-Getters that return a ScanPart with its parents
// **************************************************************
export const getScanTaskWithParents = (scan: ScanRootOptimized, groupId: string, taskId: string) => {
  const group = getScanGroup(scan, groupId);
  const task = getScanTaskFromGroup(group, taskId);
  return { group, task };
};

export const getScanOptionWithParents = (scan: ScanRootOptimized, groupId: string, taskId: string, optionId: string) => {
  const group = getScanGroup(scan, groupId);
  const task = getScanTaskFromGroup(group, taskId);
  const option = getScanOptionFromTask(task, optionId);
  return { group, task, option };
};

export const getScanLineItemWithParents = (scan: ScanRootOptimized, groupId: string, taskId: string, optionId: string, lineItemId: string) => {
  const group = getScanGroup(scan, groupId);
  const task = getScanTaskFromGroup(group, taskId);
  const option = getScanOptionFromTask(task, optionId);
  const lineItem = getScanLineItemFromOption(option, lineItemId);
  return { group, task, option, lineItem };
};

// **************************************************************
// SCOPE FUNCTIONS
// **************************************************************
/**
 * Get the scope from the values
 * @param values - The values to get the scope from - should already be calculated
 * @param parentScope - The parent scope to merge with
 * @returns The scope
 */
export const getScopeFromValues = (values: ScanValues, parentScope?: Scope): Scope => {
  const scope: Scope = { ...parentScope };
  Object.entries(values).forEach(([key, value]) => {
    const effectiveValue = getEffectiveScanValue(value);
    if (isCalculatableResult(effectiveValue)) scope[key] = getValueTypeFromScanValue(effectiveValue);
  });
  return scope;
};

export const getScopeFromScanPart = (scanPart: ScanPartOptimized) => scanPart.optimized.optimizedValueExpressions.scope;
export const getScopeForScanPartChildren = (scanPart: ScanPartOptimized) => scanPart.optimized.optimizedValueExpressions.resolved;
export const isScanPartOptimized = (scanPart: ScanPart | ScanPartOptimized) => 'optimized' in scanPart;

export const getScanPartsByScanPartId = (
  scanPart: ScanPartOptimized,
  depth: number = 0,
  into: Record<string, ScanPartOptimized> = {},
): Record<string, ScanPartOptimized> => {
  into[scanPart.id] = scanPart;
  const childrenFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
  const children = (scanPart as any)[childrenFieldName] as ScanPartOptimized[];
  if (children) {
    for (const child of children) {
      getScanPartsByScanPartId(child, depth + 1, into);
    }
  }
  return into;
};
